import { useState } from 'react';
import type { NewsArticle } from './types';
import { Newspaper, Calendar } from '../../../icons';

interface NewsCardProps {
  article: NewsArticle;
}

function formatDate(raw: string): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function NewsCard({ article }: NewsCardProps) {
  // Validate URL to prevent javascript: XSS via AI-sourced article links
  const safeHref = /^https?:\/\//i.test(article.link ?? '') ? article.link : '#';
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(article.image) && !imageFailed;
  const date = formatDate(article.pub_date);
  const source = article.source || '';
  const avatarLetter = source.charAt(0).toUpperCase() || '?';

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className="w-[264px] flex-none overflow-hidden rounded-2xl border border-border bg-card no-underline"
    >
      <div className="h-[200px] w-full">
        {showImage ? (
          <img
            src={article.image}
            alt=""
            className="size-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-primary/10">
            <Newspaper size={28} className="text-primary" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <span className="flex size-6 flex-none items-center justify-center rounded-full bg-primary text-[12px] font-bold text-white">
            {avatarLetter}
          </span>
          <span className="truncate text-body-sm-regular text-muted-foreground">{source}</span>
        </div>

        <div
          className="overflow-hidden text-body-md-medium leading-snug text-foreground"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {article.title || 'Untitled article'}
        </div>

        {date && (
          <div className="flex items-center gap-1.5 text-body-sm-medium text-muted-foreground">
            <Calendar size={14} className="text-muted-foreground" />
            <span>{date}</span>
          </div>
        )}
      </div>
    </a>
  );
}
